import type { vendorApiNames } from '../names';
import type { UsernamePasswordAuthentication } from './auth';
import type {
  BaseUpdateVendorApiSettings,
  BaseUrl,
  BaseVendorApi,
  CombinedDiscovery,
  RejectUnauthorized,
} from './base';
import type {
  VendorApiHttpAdvancedSettings,
  VendorApiHttpAgentSettings,
} from './http';
import type { VendorApiSessionSettings } from './session';

export interface ViptelaSessionSettings extends VendorApiSessionSettings {
  /**
   * The session inactivity timeout in seconds.
   */
  sessionInactivityTimeoutSec?: number;
  /**
   * The Viptela client sends a message to keep an established session up and to prevent expiration of the session inactivity timeout.
   * The `sessionInactivityTimeoutSafetyFactorPct` sets a number of percent of the session inactivity timeout when such a message is sent.
   */
  sessionInactivityTimeoutSafetyFactorPct?: number;
}

export interface ViptelaDataErrorHandlingSettings {
  /**
   * The Viptela client can detect an unreachable device behind the Viptela server and resend the request again.
   * The `unreachableDeviceMaxRetry` sets how many times such a request can be resent.
   */
  unreachableDeviceMaxRetry?: number;
  /**
   * The Viptela client can detect an unreachable device behind the Viptela server and resend the request again.
   * The `unreachableDeviceBaseDelaySec` sets a base time in seconds for wait time calculation between the request attempts.
   */
  unreachableDeviceBaseDelaySec?: number;
}

export interface ViptelaDiscoverySettings {
  ompRoutesReceivedPrefixRequestLimit?: number;
  deviceTimeoutLimit?: number;
}

/**
 * Viptela API settings available from API
 */
export type PublicViptelaApiSettings = BaseVendorApi<
  (typeof vendorApiNames)['viptela']
> &
  CombinedDiscovery &
  RejectUnauthorized &
  BaseUrl &
  VendorApiHttpAgentSettings &
  ViptelaSessionSettings &
  VendorApiHttpAdvancedSettings &
  ViptelaDataErrorHandlingSettings &
  ViptelaDiscoverySettings;

/**
 * Viptela API settings available in backend
 */
export type ViptelaApiSettings = PublicViptelaApiSettings &
  UsernamePasswordAuthentication;

/**
 * Viptela API settings expected on API when updating
 */
export type UpdateViptelaApiSettings = BaseUpdateVendorApiSettings<
  PublicViptelaApiSettings,
  UsernamePasswordAuthentication
>;
